import { ConfirmModal } from '../ConfirmModal';
import { formatNumber } from '../../utils/format';
import type { ArmyComposition, AttackType } from '../../types/attack';
import type { TravelTimeBreakdown } from '../../types/attackCalculator';
import { getRaceUnitName } from '../../constants/game';
import { ATTACK_TYPES } from '../../constants/attack';
import { COLORS } from '../../constants/theme';

/**
 * SF-147 Component 8 (Phase 3 Decision 5): generalized to serve both War Room's own commit bar
 * (new behavior — War Room previously launched directly, no confirmation step) and
 * AmbushLaunchPanel. Only `modifiedOffense`/`travelTime` are required — both flows always have
 * them; every other field is optional and its row renders only when present. `dpaAfterAttack` is
 * dropped entirely (its source function, Calculator's `calculateDefensePoints`, is removed) —
 * there's no value to thread through for either flow.
 */
interface ConfirmAttackDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  attackType: AttackType;
  targetName: string;
  armyComposition: ArmyComposition;
  modifiedOffense: number;
  travelTime: TravelTimeBreakdown;
  modifiedDefense?: number | null;
  successLabel?: string;
  mercenaryTotalCost?: number;
  race?: string;
  /** Ambush's "what you can know" summary text — renders in place of the omitted outcome rows. */
  noPreviewNote?: string;
}

export function ConfirmAttackDialog({
  isOpen,
  onConfirm,
  onCancel,
  attackType,
  targetName,
  armyComposition,
  modifiedOffense,
  travelTime,
  modifiedDefense,
  successLabel,
  mercenaryTotalCost,
  race,
  noPreviewNote,
}: ConfirmAttackDialogProps) {
  const offSpecName = getRaceUnitName(race, 'offense');
  const eliteName = getRaceUnitName(race, 'elite');
  const attackTypeName = ATTACK_TYPES[attackType]?.name ?? attackType;

  const troopLines: { label: string; count: number }[] = [
    { label: 'Generals', count: armyComposition.generals },
    { label: 'Soldiers', count: armyComposition.soldiers },
    { label: offSpecName, count: armyComposition.offenseSpecialists },
    { label: eliteName, count: armyComposition.elites },
    { label: 'War Horses', count: armyComposition.warHorses },
    { label: 'Prisoners', count: armyComposition.prisoners },
    { label: 'Mercenaries', count: armyComposition.mercenaries },
  ].filter((t) => t.count > 0);

  return (
    <ConfirmModal
      isOpen={isOpen}
      onConfirm={onConfirm}
      onCancel={onCancel}
      title="Confirm Attack"
      confirmText="Launch Attack"
      cancelText="Cancel"
    >
      <div className="space-y-3 text-sm">
        <div>
          <span style={{ color: COLORS.text.tertiary }}>Style: </span>
          <span style={{ color: COLORS.text.primary }}>{attackTypeName}</span>
        </div>
        <div>
          <span style={{ color: COLORS.text.tertiary }}>Target: </span>
          <span style={{ color: COLORS.text.primary }}>{targetName}</span>
        </div>

        <div className="pt-2" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <div className="mb-1" style={{ color: COLORS.text.tertiary }}>Troops:</div>
          {troopLines.map((t) => (
            <div key={t.label} className="flex justify-between">
              <span style={{ color: COLORS.text.secondary }}>{t.label}</span>
              <span style={{ color: COLORS.text.primary }}>{formatNumber(t.count)}</span>
            </div>
          ))}
        </div>

        {!!mercenaryTotalCost && mercenaryTotalCost > 0 && (
          <div style={{ color: COLORS.warning }}>
            Mercenary cost: {formatNumber(mercenaryTotalCost)} gc
          </div>
        )}

        {attackType === 'AMBUSH' && (
          <div style={{ color: COLORS.warning }}>
            This opportunity is consumed whether you win or lose.
          </div>
        )}

        <div className="pt-2" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <div className="flex justify-between">
            <span style={{ color: COLORS.text.tertiary }}>Your Offense</span>
            <span style={{ color: COLORS.positive }}>{formatNumber(modifiedOffense)} OP</span>
          </div>
          {modifiedDefense != null && (
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.tertiary }}>Their Defense</span>
              <span style={{ color: COLORS.negative }}>{formatNumber(modifiedDefense)} DP</span>
            </div>
          )}
          {successLabel && (
            <div className="flex justify-between">
              <span style={{ color: COLORS.text.tertiary }}>Status</span>
              <span style={{ color: COLORS.text.primary }}>{successLabel}</span>
            </div>
          )}
          {noPreviewNote && (
            <div className="text-xs mt-1" style={{ color: COLORS.text.tertiary }}>
              {noPreviewNote}
            </div>
          )}
        </div>

        <div className="pt-2" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <div className="flex justify-between">
            <span style={{ color: COLORS.text.tertiary }}>Army returns in</span>
            <span style={{ color: COLORS.text.primary }}>{travelTime.finalTime} days</span>
          </div>
        </div>
      </div>
    </ConfirmModal>
  );
}
